import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Commission,
  CommissionProfile,
  parseCm2BucketValue,
} from "../../../core/models/entities/Commission";
import { LogoOptions } from "../../../core/models/entities/LogoOptions";
import { Method } from "../../../core/models/entities/TransferOptions";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { copy } from "../../../utils/functions/ObjectUtil";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { SortDirection } from "../GenericTable/Models/sortDirection";
import { useErrorToast } from "../Toast/Toast";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { BadgeChip, BadgeTone } from "../OrgShell/BadgeChip";
import { CommissionPicker } from "./commission-picker.component";
import { CommissionPickerGroup } from "./commission-picker-group.component";
import {
  CommissionOptions,
  MethodCommissions,
} from "../../screens/customer-update/component/commission-editor.component";
import {
  applyCommissionAmount,
  describeCommissionRule,
  formatCommissionRate,
  resolveAdditionalFeesRule,
  resolveCommissionRule,
  resolveHeatingRule,
} from "./commission-math";
import { DialogFrame, useDialog } from "../Dialogs/dialog-base";
import { BoxHeaderTypography } from "../Typographies/BoxHeaderTypography";
import LoadingElementIsolated from "../loading-element/loading-element-isolated";
import { FullLivePreview } from "./full-live-preview.component";
import { orderLineTitleText } from "../../screens/order-details/subcomponents/OrderLineData";

export interface CommissionEditorV2Props {
  userType: "vendor" | "customer";
  commissions?: CommissionProfile;
  onCommissionUpdate: (commission: CommissionProfile) => void;
  logoOptions: LogoOptions;
  options: CommissionOptions;
  /** Optional extra summary card (e.g. the profile's default VAT for new customers). */
  extraSummaryCard?: React.ReactNode;
}

/**
 * Commission editor v2: summary rate cards, per-type method table with
 * rule-source badges, and a live example whose math replicates the API's
 * PriceAdjustmentService exactly (see commission-math.ts).
 *
 * The underlying data flow is unchanged from the original CommissionEditor:
 * the same CommissionProfile shape is edited and saved through the same
 * endpoints; only the presentation is new.
 */
export function CommissionEditorV2({
  userType,
  commissions: source,
  onCommissionUpdate,
  logoOptions,
  options,
  extraSummaryCard,
}: CommissionEditorV2Props) {
  const { t } = useTranslation();
  const { currency } = useProfileContext();
  const commissions: CommissionProfile = source
    ? copy(source)
    : ({ typeOnly: [], methods: [] } as unknown as CommissionProfile);
  commissions.typeOnly = commissions.typeOnly ?? [];
  commissions.methods = commissions.methods ?? [];

  const overAll: Commission =
    commissions.overAll ??
    ({ type: "OverAll", unit: "%", value: 0 } as Commission);
  const additionalFees: Commission =
    commissions.additionalFees ??
    ({ type: "AdditionalFees", unit: "%", value: 0 } as Commission);
  const heating: Commission =
    commissions.heating ??
    ({ type: "Heating", unit: "%", value: 0 } as Commission);

  const [typeSelectedIndex, setTypeSelectedIndex] = useState(0);
  const selectedType = options.types[typeSelectedIndex];

  // ----- Updates (same data flow as the original editor) -----

  function updateOverAll(commission: Commission) {
    commissions.overAll = commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateAdditionalFees(commission: Commission) {
    commissions.additionalFees =
      commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateHeating(commission: Commission) {
    commissions.heating = commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateTypeOnly(commission: Commission) {
    commissions.typeOnly = commissions.typeOnly.filter(
      (to) => to.typeKey !== commission.typeKey
    );
    if (commission.value !== 0) {
      commissions.typeOnly.push(commission);
    }
    // Method rules of this type now inherit the new type value (existing flow).
    commissions.methods = commissions.methods.filter(
      (m) => m.typeKey !== commission.typeKey
    );
    onCommissionUpdate(commissions);
  }

  function updateMethod(methodCommissions: MethodCommissions) {
    const entries = [methodCommissions.base, ...methodCommissions.sizeMethods];
    for (const entry of entries) {
      commissions.methods = commissions.methods.filter(
        (m) =>
          m.methodKey !== entry.methodKey ||
          m.maxDimensions !== entry.maxDimensions
      );
      if (entry.value !== 0) {
        commissions.methods.push(entry);
      }
    }
    onCommissionUpdate(commissions);
  }

  // ----- Derived views -----

  function typeCommissionFor(typeKey: string): Commission {
    return (
      commissions.typeOnly.find((t) => t.typeKey === typeKey) ?? {
        type: "TypeOnly",
        typeKey,
        value: overAll.value,
        unit: overAll.unit,
        maxDimensions: null,
        maxSizeCM2: null,
        maxSize: null,
        methodKey: undefined,
      }
    );
  }

  /** Builds the base+sizes view the per-method picker edits (existing flow).
   *  Size rows keep the bucket label in maxDimensions for identity in the
   *  pickers; for cm2 price groups they also carry the numeric bucket in
   *  maxSizeCM2 plus the method's NAV priceType, which is what the API price
   *  engine actually matches on (the save payload clears the label). */
  function methodCommissionsFor(method: Method): MethodCommissions {
    const typeFallback = typeCommissionFor(method.type);
    const base: Commission = commissions.methods.find(
      (m) => m.methodKey === method.methodKey && !m.maxDimensions
    ) ?? {
      value: typeFallback.value,
      maxDimensions: null,
      methodKey: method.methodKey,
      typeKey: method.type,
      type: "Method",
      unit: typeFallback.unit,
      maxSize: null,
      maxSizeCM2: null,
      priceType: String(method.priceType),
    };
    const sizeMethods = method.options.map(
      (size) =>
        commissions.methods.find(
          (m) => m.methodKey === method.methodKey && m.maxDimensions === size
        ) ?? {
          value: base.value,
          maxDimensions: size,
          methodKey: method.methodKey,
          typeKey: method.type,
          type: "Method",
          unit: base.unit,
          maxSize: null,
          maxSizeCM2: parseCm2BucketValue(size),
          priceType: String(method.priceType),
        }
    );
    return { base, sizeMethods };
  }

  interface MethodRow {
    method: Method;
    effective: Commission;
    source: "Method" | "Type" | "Default" | "None";
    overriddenSizes: number;
  }

  function rowFor(method: Method): MethodRow {
    const explicit = commissions.methods.filter(
      (m) => m.methodKey === method.methodKey
    );
    const explicitBase = explicit.find((m) => !m.maxDimensions);
    const overriddenSizes = explicit.filter((m) => !!m.maxDimensions).length;
    const type = commissions.typeOnly.find((t) => t.typeKey === method.type);

    if (explicitBase || overriddenSizes > 0) {
      return {
        method,
        effective: explicitBase ?? type ?? overAll,
        source: "Method",
        overriddenSizes,
      };
    }
    if (type) {
      return { method, effective: type, source: "Type", overriddenSizes: 0 };
    }
    if (overAll.value !== 0) {
      return {
        method,
        effective: overAll,
        source: "Default",
        overriddenSizes: 0,
      };
    }
    return { method, effective: overAll, source: "None", overriddenSizes: 0 };
  }

  const methodsOfSelectedType = selectedType
    ? options.transfers
        .filter((m) => m.type === selectedType.typeKey)
        .sort((a, b) => {
          const aKey = a.methodKey?.toUpperCase() ?? "";
          const bKey = b.methodKey?.toUpperCase() ?? "";
          if (aKey === "STANDARD") return -1;
          if (bKey === "STANDARD") return 1;
          return aKey.localeCompare(bKey);
        })
    : [];

  // ----- Method override dialog -----
  const overrideDialog = useDialog();
  const [overrideMethod, setOverrideMethod] = useState<Method | undefined>();

  function openOverride(method: Method) {
    setOverrideMethod(method);
    overrideDialog.openDialog();
  }

  const sourceTone: Record<MethodRow["source"], BadgeTone> = {
    Method: "blue",
    Type: "green",
    Default: "gray",
    None: "gray",
  };

  // Display labels only - the source values themselves are logic keys.
  const sourceLabel: Record<MethodRow["source"], string> = {
    Method: t("compCommission.sourceMethod"),
    Type: t("compCommission.sourceType"),
    Default: t("compCommission.sourceDefault"),
    None: t("compCommission.sourceNoRule"),
  };

  return (
    <div className="space-y-8">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <RateCard
          title={t("compCommission.defaultCommission")}
          description={
            userType === "customer"
              ? t("compCommission.defaultCommissionDescCustomer")
              : t("compCommission.defaultCommissionDescVendor")
          }
        >
          <CommissionPicker commission={overAll} onChange={updateOverAll} />
        </RateCard>
        <RateCard
          title={t("compCommission.extraFees")}
          description={t("compCommission.extraFeesDesc")}
        >
          <CommissionPicker
            commission={additionalFees}
            onChange={updateAdditionalFees}
          />
        </RateCard>
        <RateCard
          title={t("compCommission.heating")}
          description={t("compCommission.heatingDesc")}
        >
          <CommissionPicker commission={heating} onChange={updateHeating} />
        </RateCard>
        {extraSummaryCard}
      </div>

      <p className="rounded-md border border-sky-100 bg-sky-50 px-4 py-2.5 text-xs text-sky-800">
        {t("compCommission.rulesHowTitle")}{" "}
        <span className="font-semibold">{t("compCommission.ruleSize")}</span> →{" "}
        <span className="font-semibold">{t("compCommission.ruleMethod")}</span>{" "}
        → <span className="font-semibold">{t("compCommission.ruleType")}</span>{" "}
        →{" "}
        <span className="font-semibold">{t("compCommission.ruleDefault")}</span>
        . {t("compCommission.rulesHowTail")}
      </p>

      {/* Type tabs */}
      <div>
        <div className="flex flex-wrap gap-2 border-b pb-2">
          {options.types.map((type, index) => {
            const count = options.transfers.filter(
              (m) => m.type === type.typeKey
            ).length;
            const active = index === typeSelectedIndex;
            return (
              <button
                key={type.typeKey}
                type="button"
                onClick={() => setTypeSelectedIndex(index)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-10 font-semibold text-primary-100"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {type.nickname || type.typeKey}
                <span className="ml-1.5 text-xs text-gray-400">{count}</span>
              </button>
            );
          })}
        </div>

        {selectedType && (
          <div className="mt-4 space-y-4">
            <div className="flex items-end justify-between gap-4">
              <CommissionPicker
                labelText={t("compCommission.allTypeMethods", {
                  type: selectedType.nickname || selectedType.typeKey,
                })}
                commission={typeCommissionFor(selectedType.typeKey)}
                onChange={updateTypeOnly}
              />
              <p className="max-w-md text-xs text-gray-500">
                {t("compCommission.typeRateReplacesOverrides")}
              </p>
            </div>

            <div className="overflow-x-auto rounded border">
              <table className="w-full table-auto">
                <thead className="bg-[#FCFDFE] text-left">
                  <tr>
                    <th className="py-3 pl-4 text-xs font-medium text-gray-600">
                      {t("compCommission.method")}
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-600">
                      {t("compCommission.customerPays")}
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-600">
                      {t("compCommission.ruleSource")}
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-600">
                      {t("compCommission.sizes")}
                    </th>
                    <th className="w-24 px-4 py-3 text-xs font-medium text-gray-600" />
                  </tr>
                </thead>
                <tbody>
                  {methodsOfSelectedType.map((method) => {
                    const row = rowFor(method);
                    return (
                      <tr
                        key={method.methodKey}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="py-2 pl-4 text-xs capitalize">
                          {method.displayName?.toLowerCase() ||
                            method.methodKey}
                        </td>
                        <td className="px-4 py-2 text-xs font-semibold text-gray-700">
                          {row.source === "None"
                            ? t("compCommission.basePrice")
                            : t("compCommission.baseRate", {
                                rate: formatCommissionRate(
                                  row.effective,
                                  currency
                                ),
                              })}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <BadgeChip tone={sourceTone[row.source]}>
                            {sourceLabel[row.source]}
                          </BadgeChip>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {t("compCommission.bucketCount", {
                            count: method.options.length,
                          })}
                          {row.overriddenSizes > 0 &&
                            ` · ${t("compCommission.customCount", {
                              count: row.overriddenSizes,
                            })}`}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <SecondaryButton onClick={() => openOverride(method)}>
                            {t("compCommission.override")}
                          </SecondaryButton>
                        </td>
                      </tr>
                    );
                  })}
                  {methodsOfSelectedType.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-xs text-gray-500"
                      >
                        {t("compCommission.noMethodsForType")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Method override dialog (existing per-method picker) */}
      {overrideMethod && (
        <DialogFrame
          dialogOption={overrideDialog}
          onClose={() => setOverrideMethod(undefined)}
        >
          <div className="min-h-[24rem] space-y-4">
            <BoxHeaderTypography>
              {t("compCommission.overrideDialogTitle", {
                method:
                  overrideMethod.displayName?.toLowerCase() ||
                  overrideMethod.methodKey,
              })}
            </BoxHeaderTypography>
            <p className="max-w-sm text-xs text-gray-500">
              {t("compCommission.overrideDialogDesc")}
            </p>
            <CommissionPickerGroup
              key={overrideMethod.methodKey}
              labelText={
                overrideMethod.displayName?.toLowerCase() ||
                overrideMethod.methodKey
              }
              method={overrideMethod}
              commissions={methodCommissionsFor(overrideMethod)}
              updateCommission={updateMethod}
            />
          </div>
        </DialogFrame>
      )}

      {/* Live example v2 */}
      <LiveExampleV2
        commissions={commissions}
        transfers={options.transfers}
        logoOptions={logoOptions}
      />
    </div>
  );
}

export function RateCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-md border bg-gray-150 p-4">
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
      <p className="mb-3 mt-0.5 flex-1 text-xs text-gray-500">{description}</p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live example v2
// ---------------------------------------------------------------------------

interface LiveExampleV2Props {
  commissions: CommissionProfile;
  transfers: Method[];
  logoOptions: LogoOptions;
}

function LiveExampleV2({
  commissions,
  transfers,
  logoOptions,
}: LiveExampleV2Props) {
  const { t } = useTranslation();
  const { currency } = useProfileContext();
  const { logoRepository } = useRepositoriesContext();

  // ----- Example-logo search (same flow as the original live example) -----
  const [skuSearch, setSkuSearch] = useState("");
  const [activeSkuSearch, setActiveSkuSearch] = useState("");
  const [displayedLogo, setDisplayedLogo] = useState<BaseLogo | null>(null);
  const previousSearchRef = useRef("");

  // Classification 3 = "Logo" (ImageLogo/static logos) - a data value, not UI copy.
  const { data: logoData, isLoading: isLoadingLogo } =
    logoRepository.useAllLogos({
      searchTerm: activeSkuSearch,
      filterBy: [{ field: "classification", value: "3" }],
      sortBy: { field: "createdAtUtc", sortDirection: SortDirection.DESC },
      page: { size: 1, index: 1 },
    });

  const fetchedLogo = logoData?.results?.[0];

  useEffect(() => {
    if (isLoadingLogo) return;
    if (fetchedLogo) {
      setDisplayedLogo(fetchedLogo);
      previousSearchRef.current = activeSkuSearch;
    } else if (
      activeSkuSearch &&
      activeSkuSearch !== previousSearchRef.current
    ) {
      useErrorToast(
        t("compCommission.logoItemNoNotFound", { itemNo: activeSkuSearch })
      );
      setActiveSkuSearch(previousSearchRef.current);
    }
  }, [fetchedLogo, isLoadingLogo, activeSkuSearch]);

  function handleSkuSearch() {
    if (skuSearch.trim()) {
      setActiveSkuSearch(skuSearch.trim());
      setSkuSearch("");
    }
  }

  const exampleLogo = displayedLogo;

  // ----- API-replica rule resolution -----
  const rule = useMemo(
    () => resolveCommissionRule(commissions, exampleLogo, transfers),
    [commissions, exampleLogo, transfers]
  );
  const feesRule = resolveAdditionalFeesRule(commissions);
  const heatingRule = resolveHeatingRule(commissions);

  const prices = useMemo(
    () =>
      [...(exampleLogo?.product?.prices ?? [])].sort(
        (a, b) => a.minQuantity - b.minQuantity
      ),
    [exampleLogo]
  );

  const fees = exampleLogo?.product?.additionalFees ?? [];

  // Base heating value depends on currency (DKK uses the higher value) -
  // mirrors the original live example.
  const baseHeatingValue = currency === "DKK" ? 9.25 : 1.25;

  const fullPreviewDialog = useDialog();

  const fmt = (value: number) => `${currency} ${value.toFixed(2)}`;

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h4 className="flex-1 text-sm font-semibold text-gray-700">
          {t("compCommission.liveExample")}
        </h4>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder={t("compCommission.enterItemNoPlaceholder")}
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSkuSearch()}
            className="w-32 rounded border border-borderGray px-2 py-1 text-xs placeholder:italic focus:border-gray-400 focus:outline-none"
          />
          <button
            onClick={handleSkuSearch}
            className="rounded border border-borderGray bg-slate-100 px-2 py-1 text-xs hover:bg-slate-200"
          >
            {t("search")}
          </button>
          <SecondaryButton
            onClick={fullPreviewDialog.openDialog}
            disabled={!exampleLogo}
          >
            {t("compCommission.openFullLivePreview")}
          </SecondaryButton>
        </div>
      </div>

      {!exampleLogo && !isLoadingLogo ? (
        <p className="py-6 text-center text-xs italic text-gray-400">
          {t("compCommission.noExampleLogo")}
        </p>
      ) : (
        <div className="relative">
          {isLoadingLogo && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/80">
              <LoadingElementIsolated />
            </div>
          )}

          <div className="flex flex-wrap items-start gap-6">
            <div className="min-w-[16rem] flex-1 space-y-4">
              <div className="flex items-center gap-3 rounded-md border bg-gray-150 p-3">
                <img
                  src={
                    exampleLogo?.displayImage?.highResUrl ||
                    exampleLogo?.displayImage?.lowResUrl
                  }
                  alt={exampleLogo?.title || t("compCommission.exampleLogoAlt")}
                  className="h-14 w-14 rounded bg-white object-contain p-1"
                />
                <div className="min-w-0 text-xs text-gray-600">
                  <p className="truncate font-semibold">
                    {exampleLogo?.title}
                    {exampleLogo?.product?.sku && (
                      <span className="font-normal text-gray-400">
                        {" "}
                        ({exampleLogo.product.sku})
                      </span>
                    )}
                  </p>
                  <p className="capitalize">
                    {exampleLogo?.transferMethod?.type?.toLowerCase()} ·{" "}
                    {exampleLogo?.transferMethod?.displayName?.toLowerCase()} ·{" "}
                    {exampleLogo?.heightMm}×{exampleLogo?.widthMm} mm
                  </p>
                  <p className="mt-1 text-sky-700">
                    {t("compCommission.ruleApplied")}{" "}
                    {describeCommissionRule(rule, transfers, t)}
                    {rule.source !== "none" &&
                      ` (${formatCommissionRate(rule.commission, currency)})`}
                  </p>
                </div>
              </div>

              {/* Fee rows with the rate that hit them */}
              <table className="w-full table-auto">
                <tbody>
                  {fees.map((fee) => {
                    const feeTier = [...(fee.prices ?? [])].sort(
                      (a, b) => a.minQuantity - b.minQuantity
                    )[0];
                    const yourFee = feeTier?.adjustedPrice ?? 0;
                    return (
                      <tr key={fee.name} className="border-b">
                        <td className="py-1.5 text-xs text-gray-600">
                          {orderLineTitleText(fee.name, t)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs text-gray-500">
                          {fmt(yourFee)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700">
                          {fmt(
                            applyCommissionAmount(yourFee, feesRule.commission)
                          )}
                        </td>
                        <td className="py-1.5 text-right text-xs text-gray-400">
                          {feesRule.source === "none"
                            ? t("compCommission.noMarkup")
                            : t("compCommission.extraFeesRate", {
                                rate: formatCommissionRate(
                                  feesRule.commission,
                                  currency
                                ),
                              })}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td className="py-1.5 text-xs text-gray-600">
                      {t("compCommission.heating")}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-500">
                      {fmt(baseHeatingValue)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700">
                      {fmt(
                        applyCommissionAmount(
                          baseHeatingValue,
                          heatingRule.commission
                        )
                      )}
                    </td>
                    <td className="py-1.5 text-right text-xs text-gray-400">
                      {heatingRule.source === "none"
                        ? t("compCommission.noMarkup")
                        : t("compCommission.heatingRate", {
                            rate: formatCommissionRate(
                              heatingRule.commission,
                              currency
                            ),
                          })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Quantity tier table */}
            <div className="min-w-[18rem] flex-1">
              <table className="w-full table-auto">
                <thead className="text-left">
                  <tr className="border-b">
                    <th className="py-2 text-xs font-medium text-gray-600">
                      {t("compCommission.qty")}
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-600">
                      {t("compCommission.yourPricePerPc")}
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-600">
                      {t("compCommission.customerPerPc")}
                    </th>
                    <th className="py-2 text-right text-xs font-medium text-gray-600">
                      {t("compCommission.marginPerPc")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((price, idx) => {
                    const customer = applyCommissionAmount(
                      price.adjustedPrice,
                      rule.commission
                    );
                    return (
                      <tr key={idx} className="border-b">
                        <td className="py-1.5 text-xs text-gray-600">
                          {price.minQuantity || 1}+
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs text-gray-500">
                          {fmt(price.adjustedPrice)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700">
                          {fmt(customer)}
                        </td>
                        <td className="py-1.5 text-right text-xs text-emerald-700">
                          {fmt(customer - price.adjustedPrice)}
                        </td>
                      </tr>
                    );
                  })}
                  {prices.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-4 text-center text-xs italic text-gray-400"
                      >
                        {t("compCommission.noPricesSet")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Full live preview (combined price table, all buckets) */}
      <DialogFrame dialogOption={fullPreviewDialog}>
        <FullLivePreview
          logo={exampleLogo}
          logoOptions={logoOptions}
          commissions={commissions}
          transfers={transfers}
          baseHeatingValue={baseHeatingValue}
        />
      </DialogFrame>
    </div>
  );
}

